import * as PIXI from "pixi.js"
import { NodeData, PointProperty } from "./types"
import vavURL from "./VAV.svg"
// import dataVis from "./dataVis.jpg"
import ScrollManager from "../ScrollManager"
import DetailItem from "../DetailItem"
import Settings from "../Settings"

export default class Node extends PIXI.Container {
    name: string
    id: string
    childNodes: Node[] = []
    properties: PointProperty[] = []
    parentNode?: Node
    targetX: number = 0
    targetY: number = 0
    targetScale: number = 1
    mod: number = 0
    collapsed: boolean = false
    container!: PIXI.Container
    detailPanel!: PIXI.Container
    detailSafeZone!: PIXI.Graphics
    detailItems: DetailItem[] = []
    titleText!: PIXI.Text
    bgBox!: PIXI.Graphics
    propertyIndicator!: PIXI.Graphics
    indicatorDots: PIXI.Graphics[] = []
    dotContainer!: PIXI.Container
    warningIndicator!: PIXI.Graphics
    toggle!: PIXI.Graphics

    constructor(data: NodeData, parentNode?: Node) {
        super()
        this.name = data.name
        this.id = data.id
        this.properties = data.properties
        this.createChildNodes(this, data)
        this.parentNode = parentNode

        // this.cacheAsBitmap = true
        this.interactive = true
        this.buttonMode = true

        this.setupView()
    }

    createChildNodes(node: Node, data: NodeData) {
        for (const childData of data.childNodes) {
            const childNode = new Node(childData, node)
            node.childNodes.push(childNode)
        }
    }

    setupView() {
        const width = 150
        const height = 90

        // container
        const container = new PIXI.Container()
        this.addChild(container)
        container.pivot.set(width / 2, 0)
        // container.x -= width / 2
        // container.y -= height / 2
        this.container = container

        // background box
        const bgBox = new PIXI.Graphics()
        bgBox.beginFill(0xf6f7fb)
        // graphics.beginFill(0xcccccc)
        bgBox.drawRoundedRect(0, 0, width, height, 8)
        bgBox.endFill()
        bgBox.pivot.set(width / 2, 0)
        bgBox.x = width / 2
        bgBox.cacheAsBitmap = true

        container.addChild(bgBox)
        this.bgBox = bgBox

        // name text
        // possible need to inherit font style
        const style = new PIXI.TextStyle({
            fontFamily: "Red Hat Text",
            fontSize: 18,
            fill: "#2F3A45",
        })
        const titleText = new PIXI.Text(this.name, style)
        titleText.resolution = 2
        container.addChild(titleText)
        titleText.anchor.set(0.5, 0.5)
        titleText.x = width / 2
        titleText.y = height - titleText.height
        titleText.cacheAsBitmap = true
        this.titleText = titleText

        // node icon
        const baseTexture = new PIXI.BaseTexture(vavURL, { resolution: 2 })
        const sprite = new PIXI.Sprite(new PIXI.Texture(baseTexture))
        container.addChild(sprite)
        sprite.scale.x = 1
        sprite.scale.y = 1
        sprite.anchor.set(0.5)
        sprite.x = width / 2
        sprite.y = 30

        // expand toggle
        const toggle = new PIXI.Graphics()
        toggle.beginFill(0x798793)
        toggle.drawRoundedRect(-10, 0, 20, 20, 4)
        toggle.endFill()
        this.container.addChild(toggle)
        toggle.y = height - 5
        toggle.x = width / 2
        toggle.visible = false
        this.toggle = toggle

        const style2 = new PIXI.TextStyle({
            fontFamily: "Helvetica",
            fontSize: 20,
            fill: "0xFFFFFF",
        })
        const text2 = new PIXI.Text("+", style2)
        titleText.resolution = 2
        toggle.addChild(text2)
        text2.x = -text2.width / 2
        text2.y = -5

        // toggle.cacheAsBitmap = true

        // detail panel
        const detailWidth = width * 2
        const detailHeight = 40
        const detailMargin = 10

        const panel = new PIXI.Container()
        this.container.addChild(panel)
        panel.x = width / 2 - width
        panel.y = 100
        // panel.scale.y = 0.5
        this.detailPanel = panel

        // detail safe zone
        const detailSafeZone = new PIXI.Graphics()
        detailSafeZone.beginFill(0x0000ff)
        // panel.drawRoundedRect(-width, height + 10, width * 2, 200, 8)
        detailSafeZone.drawRect(0, 0, detailWidth, Settings.nodeRowSpacing - 250)
        detailSafeZone.endFill()
        panel.addChildAt(detailSafeZone, 0)
        detailSafeZone.visible = false
        detailSafeZone.cacheAsBitmap = true
        this.detailSafeZone = detailSafeZone

        // detail items
        for (let i = 0; i < this.properties.length; i++) {
            const property = this.properties[i]
            const detailItem = new DetailItem(property, detailWidth, detailHeight)
            panel.addChild(detailItem)
            this.detailItems.push(detailItem)
            detailItem.y = i * (detailHeight + detailMargin)
            detailItem.cacheAsBitmap = true
        }

        // property indicator
        const propIndicator = new PIXI.Graphics()
        propIndicator.beginFill(0xffffff)
        propIndicator.lineStyle(1, 0xbcc3c9, 0.5)
        propIndicator.drawRoundedRect(-20, 0, 40, 20, 20)
        propIndicator.endFill()
        panel.addChild(propIndicator)
        propIndicator.x = detailSafeZone.width / 2
        if (this.properties.length === 0) {
            propIndicator.visible = false
        }
        this.propertyIndicator = propIndicator

        // property indicator dots
        const dotContainer = new PIXI.Container()
        this.propertyIndicator.addChild(dotContainer)
        dotContainer.y = propIndicator.height / 2
        this.dotContainer = dotContainer
        for (let i = 0; i < this.properties.length; i++) {
            const property = this.properties[i]
            const dot = new PIXI.Graphics()
            const fillColor = property.hasWarning ? 0xdc583d : 0x6dac84
            dot.beginFill(fillColor)
            dot.drawCircle(0, 0, 3)
            dot.endFill()
            dot.cacheAsBitmap = true
            dotContainer.addChild(dot)
            this.indicatorDots.push(dot)
            dot.x = i * 10
        }

        // singular warning indicator - used for when indicator dots are hidden at small zooms
        const warningIndicator = new PIXI.Graphics()
        warningIndicator.beginFill(0xdc583d)
        warningIndicator.drawCircle(0, 0, 6)
        warningIndicator.endFill()
        // warningIndicator.cacheAsBitmap = true
        container.addChild(warningIndicator)
        warningIndicator.x = width / 2
        warningIndicator.y = 60
        warningIndicator.visible = this.hasWarning()
        this.warningIndicator = warningIndicator

        // interaction
        this.on("click", () => {
            if (!ScrollManager.didDrag && this.childNodes.length > 0) {
                this.collapsed = !this.collapsed

                // this.cacheAsBitmap = false
                toggle.visible = this.collapsed
                // this.cacheAsBitmap = true

                var event = new CustomEvent("NodeWasToggled", { detail: this })
                document.dispatchEvent(event)
            }
        })
    }

    hasWarning(): boolean {
        let hasWarning = false
        for (const property of this.properties) {
            if (property.hasWarning) {
                hasWarning = true
            }
        }
        return hasWarning
    }

    updatePropIndicator() {
        let visibleCount = 0
        for (let i = 0; i < this.detailItems.length; i++) {
            this.indicatorDots[i].visible = !this.detailItems[i].visible
            visibleCount += this.detailItems[i].visible ? 1 : 0
        }
        this.dotContainer.x = -visibleCount * 10 - this.dotContainer.width / 2 + 3

        const targetWidth = (this.detailItems.length - visibleCount) * 10 + 12
        this.propertyIndicator.clear()
        this.propertyIndicator.beginFill(0xffffff)
        this.propertyIndicator.lineStyle(1, 0xbcc3c9, 0.5)
        this.propertyIndicator.drawRoundedRect(-targetWidth / 2, 0, targetWidth, 20, 20)
        this.propertyIndicator.endFill()

        this.propertyIndicator.visible = visibleCount !== this.detailItems.length
    }

    isLeaf(): boolean {
        return !(this.childNodes.length > 0)
    }

    isLeftMost(): boolean {
        // if parent is undefined, we are at the root node
        if (this.parentNode === undefined) {
            return true
        }

        // this should never happen, but we check for safety
        if (this.parentNode.childNodes.length === 0) {
            return true
        } else {
            return this === this.parentNode.childNodes[0]
        }
    }

    isRightMost(): boolean {
        if (this.parentNode === undefined) {
            return false
        }

        return this === this.parentNode.childNodes[this.parentNode.childNodes.length - 1]
    }

    getPreviousSibling(): Node | undefined {
        if (this.parentNode === undefined) {
            return undefined
        }

        const thisIndex = this.parentNode.childNodes.indexOf(this)
        return this.parentNode.childNodes[thisIndex - 1]
    }

    getChildCount(): number {
        return this.childNodes.length
    }

    getLeftMostChild(): Node | undefined {
        if (this.childNodes.length === 0) {
            return undefined
        }

        return this.childNodes[0]
    }

    getRightMostChild(): Node | undefined {
        if (this.childNodes.length === 0) {
            return undefined
        }

        return this.childNodes[this.childNodes.length - 1]
    }

    getLeftMostSibling(): Node | undefined {
        if (this.parentNode === undefined) {
            return undefined
        }

        return this.parentNode.childNodes[0]
    }

    getNextSibling(): Node | undefined {
        if (this.parentNode === undefined) {
            return undefined
        }

        const thisIndex = this.parentNode.childNodes.indexOf(this)
        if (thisIndex === this.parentNode.childNodes.length - 1) {
            return undefined
        } else {
            return this.parentNode.childNodes[thisIndex + 1]
        }
    }
}
